/*
 * video-convert-plan.js — the brain of /video-converter.
 *
 * WHY THIS EXISTS AS ITS OWN MODULE
 *
 * Every browser video tool we run fails in the same four ways (measured over
 * 30 days across nine video tools, ~5.5 k errors):
 *
 *   ~640  ffmpeg-exit / "Could not find tag for codec" / bad args
 *   ~578  FILE_LOST / NotReadableError — the handle died before we read it
 *   ~550  memory access out of bounds / Array buffer allocation failed
 *   ~377  simd_unsupported — the CPU cannot run our WASM core AT ALL
 *
 * A converter that decodes-and-re-encodes everything walks into all four. So
 * this module answers ONE question before any work starts:
 *
 *      given THIS file and THIS target, what is the cheapest correct plan,
 *      and can this device actually execute it?
 *
 * and it answers honestly, up front, in words the page can show the user.
 *
 * THE THREE PLANS, cheapest first:
 *
 *   'remux'   — container swap only, `-c copy` on both streams. No decode, no
 *               quality loss, seconds instead of minutes, and it cannot OOM
 *               because nothing is decoded. This is the case the market
 *               charges for and never advertises: an MP4→MKV of a 2 GB file is
 *               a metadata rewrite, not a transcode.
 *   'audio'   — video copied, audio re-encoded (the target container refuses
 *               the source audio codec: PCM into MP4, AAC into WebM…).
 *   'full'    — both streams re-encoded (codec incompatible, or the user asked
 *               for a different resolution / fps / quality / size).
 *
 * The plan is DERIVED from the probe + CodecMuxRules, never guessed, and it is
 * shown to the user before they press anything — the honest version of "why
 * did this take 2 seconds and that one 4 minutes".
 *
 * Depends on (all already shipped and battle-tested):
 *   window.__videoProbe        — 3-tier metadata probe (mp4box → <video> → ffmpeg)
 *   window.CodecMuxRules       — can this codec be copied into that container
 *   window.VideoFormats        — the config/video-formats.php catalogue
 *   window.FFmpegProcessor     — execution (WASM, auto-patched by WebCodecs)
 */
(function () {
    'use strict';
    if (window.VideoConvertPlan) return;   // idempotent

    /* ── device capability, asked ONCE and asked HONESTLY ──────────────────
       377 of our video errors are "this CPU has no WASM SIMD" — discovered
       after the user waited for a 30 MB core download and picked a file. The
       converter asks first and says so at file-select. */
    var _caps = null;
    function capabilities() {
        if (_caps) return _caps;
        var simd = null;
        try {
            // The canonical 8-byte SIMD probe module (v128.const). Sync, ~0 ms.
            simd = WebAssembly.validate(new Uint8Array([
                0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0,
                10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11
            ]));
        } catch (_) { simd = false; }
        _caps = {
            wasm:      typeof WebAssembly === 'object',
            simd:      !!simd,
            webcodecs: typeof window.VideoEncoder === 'function' && typeof window.VideoDecoder === 'function',
            // Can we do ANYTHING at all? WebCodecs alone still covers re-encode
            // into mp4/webm on modern Chromium; WASM alone covers everything but
            // needs SIMD in our core build.
            get usable() { return (this.wasm && this.simd) || this.webcodecs; }
        };
        return _caps;
    }

    /* ── the plan ─────────────────────────────────────────────────────────── */

    /* Probes speak three dialects: mp4box returns RFC-6381 strings
       ('mp4a.40.2', 'avc1.42E028', 'vp09.00.10.08'), ffmpeg returns its own ids
       ('aac', 'h264'), QuickTime PCM arrives as a FourCC ('twos', 'sowt').
       CodecMuxRules already owns that mapping — reuse it through a one-track
       shim rather than writing a second table that will drift from the first.
       Measured cost of NOT doing this: MP4→MOV was planned as an audio
       re-encode because 'mp4a.40.2' is not the literal string 'aac'. */
    function normCodec(str, kind) {
        var s = String(str || '').trim().toLowerCase();
        if (!s) return '';
        var rules = window.CodecMuxRules;
        if (!rules) return s;
        try {
            return (kind === 'video'
                ? rules.videoCodecFromMp4Box([{ type: 'video', codec: s }])
                : rules.audioCodecFromMp4Box([{ type: 'audio', codec: s }])) || s;
        } catch (_) { return s; }
    }

    /* Containers whose muxer refuses anything it has no tag for. An UNKNOWN
       codec must never be optimistically copied into one of these: the copy
       dies with "Could not find tag for codec X" after the user waited.
       Matroska is the opposite — it carries anything — so unknown is safe there.
       (Measured: a MOV with QuickTime PCM whose codec the probe could not name
       was planned as a remux and failed exactly that way.) */
    var STRICT = ['mp4', 'm4v', 'mov', '3gp', 'webm', 'avi', 'flv', 'wmv', 'ts'];

    /* «Plays everywhere» for an H.264 stream is a PROFILE question: Baseline /
       Main / Extended / High are 8-bit 4:2:0 and open on every phone, TV and
       editor; High 10 / 4:2:2 / 4:4:4 are exactly the streams a player chokes
       on while Chromium plays them. The RFC-6381 string carries profile_idc in
       its first byte (avc1.PPCCLL). true / false when it says, null when the
       probe tier gave no such string (FFmpeg tier, non-ISOBMFF containers). */
    function h264Plays(codecFull) {
        var m = /^avc[13]\.([0-9a-f]{2})/i.exec(String(codecFull || ''));
        if (!m) return null;
        var p = parseInt(m[1], 16);
        return p === 66 || p === 77 || p === 88 || p === 100;
    }

    /* The source's SHORTER side — the one a «1080p» / «720p» pick names (the
       ResolutionModule contract: a portrait 1080×1920 clip IS 1080p). 0 when
       the probe could not size the picture. */
    function srcShortSide(probe) {
        var w = probe && +probe.width, h = probe && +probe.height;
        return (w > 0 && h > 0) ? Math.min(w, h) : 0;
    }

    /* The -vf for a plan's `scaleTo`: sizes the SHORTER side and cannot
       upscale. One filter for both converters — /video-converter has
       ResolutionModule on the page, /file-converter does not load it, and the
       file converter's own `scale=-2:N` sized the HEIGHT (portrait 1440×2560 on
       «1080» → 608×1080) and blew a 720p source up to 1080p on its forced
       re-encode rung. null when there is nothing to scale. */
    function scaleFilter(n) {
        n = parseInt(n, 10) || 0;
        if (!(n > 0)) return null;
        try {
            if (window.ResolutionModule && window.ResolutionModule.buildScaleFilter) {
                return window.ResolutionModule.buildScaleFilter(n);
            }
        } catch (_) {}
        return "scale=w='if(gte(ih\\,iw)\\,min(" + n + "\\,iw)\\,-2)':h='if(gte(ih\\,iw)\\,-2\\,min(" + n + "\\,ih))':flags=lanczos";
    }

    /**
     * @param {object} probe   result of window.__videoProbe(file)
     * @param {string} target  registry format key ('mp4' | 'mkv' | …)
     * @param {object} opts    {resolution, fps, quality, targetSizeMb, stripAudio,
     *                          compat, audioOnly, trim:{start,end}}
     *                          resolution — a SHORTER-SIDE target ('1080', '720'…)
     * @returns {{kind, reason, videoCopy, audioCopy, lossless, warnings[],
     *            scaleTo, keptResolution}}
     *   scaleTo        — shorter side to scale DOWN to (0 = no scaling)
     *   keptResolution — a resolution was asked and the source already meets
     *                    it, so the pick changed nothing about the pixels
     */
    function decide(probe, target, opts) {
        opts = opts || {};
        var rules = window.CodecMuxRules;
        var out = {
            kind: 'full',
            videoCopy: false,
            audioCopy: false,
            lossless: false,
            reason: 'reencode',
            warnings: [],
            scaleTo: 0,
            keptResolution: false
        };

        /* A resolution pick is a CEILING on the shorter side, never a size to
           force. Prod, 60 d, both shards: of 108 runs on the «1080p» preset, 24
           sources were already 1080p and 40 were SMALLER — all 64 went through
           a full x264 re-encode, the 40 upscaled to a bigger file with no new
           detail; «720p» did the same to 22 of 55. And `scale=-2:N` sized the
           HEIGHT, so a 1080×1920 phone clip came out 608×1080 — below the
           «1080p» it was promised. A source that already meets the pick
           changes no pixel, so it plans like no pick was made (copy when the
           codecs allow); an unknown source size keeps the conservative
           re-encode, whose filter cannot upscale either (buildScaleFilter). */
        var resN = (opts.resolution && opts.resolution !== 'same') ? (parseInt(opts.resolution, 10) || 0) : 0;
        var srcShort = srcShortSide(probe);
        var resizes = false;
        if (resN > 0) {
            if (srcShort && srcShort <= resN) out.keptResolution = true;
            else { resizes = true; out.scaleTo = resN; }
        }
        // The same for a frame rate the source already has (29.97 under «30»).
        var fpsN = (opts.fps && opts.fps !== 'same') ? (parseFloat(opts.fps) || 0) : 0;
        var srcFps = probe && +probe.fps;
        var changesFps = fpsN > 0 && !(srcFps > 0 && Math.abs(srcFps - fpsN) / fpsN < 0.02);

        /* Anything that CHANGES THE PIXELS forces a full re-encode — there is no
           honest shortcut, and pretending otherwise is how tools ship silent
           quality damage. */
        var forcesEncode =
            resizes || changesFps ||
            (opts.quality && opts.quality !== 'same') ||
            (opts.targetSizeMb > 0);

        if (forcesEncode) {
            out.reason = opts.targetSizeMb > 0 ? 'target_size'
                : resizes ? 'resolution'
                : changesFps ? 'fps' : 'quality';
            return out;
        }

        /* Stream-copy gates. `null` means "unknown codec" — NEVER assume true:
           false costs a re-encode and always works, true on an unsupported codec
           re-introduces the muxer tag failure this fleet spent a month on. */
        var vName = normCodec(probe && probe.codec, 'video');
        var aName = normCodec(probe && probe.acodec, 'audio');
        var vCopy = rules ? rules.videoCopyOk(vName, target) : null;
        var aCopy = rules ? rules.audioCopyOk(aName, target) : null;
        var strict = STRICT.indexOf(String(target).toLowerCase()) !== -1;

        /* ⚠️ "The probe named no audio codec" is NOT "the file has no audio".
           mp4box files QuickTime PCM under type=metadata, so a MOV recorded by
           a Mac screen recorder arrives with an empty acodec and a very real
           PCM track. Treating that as silence made the plan copy it into MP4,
           where it died with "Could not find tag for codec pcm_s16le" — after
           the wait. Unknown audio into a strict container therefore means
           RE-ENCODE. If the file truly is silent, `-c:a <enc>` is a no-op, so
           the conservative branch costs nothing when we were wrong. */
        var audioUnknown = !aName && !opts.stripAudio;
        var hasAudio = (!!aName || audioUnknown) && !opts.stripAudio;
        if (audioUnknown && strict) aCopy = false;

        /* An unknown VIDEO codec into a strict container: re-encode. A wrong
           "copy" here is a hard FFmpeg failure minutes in; a wrong "encode" is
           only slower. */
        if (vCopy === null && strict) vCopy = false;
        if (aCopy === null && strict) aCopy = false;

        /* compat («plays everywhere») promises H.264 8-bit 4:2:0 + AAC in MP4.
           A stream that already IS that is copied: prod 60 d, 26 of 50 compat
           runs were H.264+AAC MP4s the probe had planned as a remux, and every
           one went through x264 (11 abandoned). HEVC / VP9 / AV1, a 10-bit or
           4:2:2 profile, or a profile the probe could not read is re-encoded,
           as before — a copy there would keep the stream the player choked on. */
        if (opts.compat) {
            vCopy = (vName === 'h264' && h264Plays(probe && probe.codecFull) === true);
            if (hasAudio) aCopy = (aName === 'aac');
        }

        // Nothing about the source is known AND the target is strict: there is
        // no honest shortcut left.
        if (!vName && strict) {
            out.reason = 'source_unknown';
            out.warnings.push('probe_incomplete');
            return out;
        }

        /* ⛔ There is NO "same container ⇒ just copy" shortcut, and there must
           not be one. It existed here for exactly one test run and produced a
           hard failure: probes report every ISOBMFF file as container 'mp4', so
           a .mov holding QuickTime PCM looked like "mp4 → mp4, nothing to do",
           skipped the codec gates, and FFmpeg died with "Could not find tag for
           codec pcm_s16le". Same-container is not a special case — it is the
           ordinary case where both gates happen to say copy, and letting the
           gates say it is the only way that stays true for a file written by a
           different muxer. */

        if (vCopy === true) {
            out.videoCopy = true;
            if (!hasAudio || aCopy === true) {
                out.kind = 'remux';
                out.audioCopy = true;
                out.lossless = true;
                out.reason = 'copy_both';
            } else if (aCopy === false) {
                out.kind = 'audio';
                out.reason = 'audio_incompatible';
                out.lossless = false;   // video is bit-exact, audio is not
            } else {
                // Unknown audio codec: copy and let the reactive retry ladder
                // catch a muxer refusal — the documented behaviour of a null gate.
                out.kind = 'remux';
                out.audioCopy = true;
                out.reason = 'copy_unknown_audio';
                out.warnings.push('audio_codec_unknown');
            }
            return out;
        }

        out.reason = vCopy === false ? 'video_incompatible' : 'video_codec_unknown';
        return out;
    }

    /* ── size / time estimate, from the probe, not from hope ───────────────── */

    /** Bitrate (kbps) needed to land a `sizeMb` file of `durationSec`. */
    function bitrateForSize(sizeMb, durationSec, audioKbps) {
        if (!(sizeMb > 0) || !(durationSec > 0)) return 0;
        var totalKbit = sizeMb * 8192;                    // MB → kbit
        var v = (totalKbit / durationSec) - (audioKbps || 128);
        // Below ~120 kbps h264 stops being watchable at any resolution; refuse
        // rather than deliver mush and call it a conversion.
        return Math.max(120, Math.round(v));
    }

    /** Rough output size (bytes) for a plan — for the "what will I get" line. */
    function estimateSize(probe, plan, opts) {
        var src = (probe && probe.size) || 0;
        if (plan.kind === 'remux') return src;            // container swap ≈ same
        if (opts && opts.targetSizeMb > 0) return opts.targetSizeMb * 1048576;
        var dur = (probe && probe.duration) || 0;
        if (!dur) return 0;
        var vk = opts && opts.videoKbps ? opts.videoKbps : 0;
        if (!vk) return 0;
        return Math.round(((vk + 128) * 1024 * dur) / 8);
    }

    /* ── which ENGINE runs a full re-encode ─────────────────────────────────
       The hardware lane is the shared WebCodecsPipeline (demux → VideoDecoder →
       VideoEncoder → mp4-muxer), the same one /video-speed, /video-crop and
       /video-compressor run; FFmpeg-WASM x264 stays the fallback for everything
       it cannot take. Until 2026-09-30 this tool had no such lane at all — it
       was designed around an FFmpegProcessor auto-patch (webcodecs-encoder.js)
       that had been deleted on 2026-05-02, so every full re-encode ran
       single-threaded x264: prod 60 d, p50 123 s / p90 36 min on desktop,
       p50 189 s on phones, 41 % of phone re-encodes abandoned.
       Returns null when the hardware lane can take the job, else the reason —
       each one reported (`hw_skipped{reason}`) so a lane that silently stops
       serving people shows up as a reason, not as a hole.
       env: {pipeline, canDemux, webm} — what this page and file can do. */
    function hwSkipReason(probe, plan, target, opts, env) {
        opts = opts || {}; env = env || {};
        if (!plan || plan.kind !== 'full') return 'not_full';
        if (!env.pipeline) return 'no_webcodecs';
        if (!env.canDemux) return 'container_' + String((probe && probe.container) || 'unknown').slice(0, 8);
        var t = String(target || '').toLowerCase();
        if (!/^(mp4|m4v|mov|mkv|webm)$/.test(t)) return 'fmt_' + t;
        if (t === 'webm' && !env.webm) return 'no_webm_encoder';
        // The pipeline can only LOWER a frame rate (maxFps); raising one is
        // frame duplication x264 does and the pipeline does not.
        var fpsN = (opts.fps && opts.fps !== 'same') ? (parseFloat(opts.fps) || 0) : 0;
        if (fpsN > 0 && probe && probe.fps > 0 && fpsN > probe.fps * 1.02) return 'fps_up';
        if (!opts.stripAudio) {
            // The pipeline carries ONE audio track; FFmpeg keeps them all (-map 0:a?).
            if (probe && probe.audioTracks > 1) return 'multi_audio';
            // …and down-mixes surround to stereo.
            if (probe && probe.audioChannels > 2) return 'surround';
            // Sound WebCodecs cannot decode (AC-3, DTS, AMR …): the pipeline
            // would refuse it (audio-codec-unsupported) — say so before the
            // click, so the estimate is the FFmpeg one. Camera PCM and G.711 in
            // mp4/mov the pipeline reads itself since 22.10.2026 — it answers
            // that (env.carriesAudio = WebCodecsPipeline.carriesAudioCodec).
            var a = normCodec(probe && probe.acodec, 'audio');
            if (/^(pcm_|adpcm|ac3$|eac3$|dts|truehd|amr|wma)/.test(a) && !(typeof env.carriesAudio === 'function' && env.carriesAudio(a))) return 'audio_' + a.slice(0, 12);
        }
        return null;
    }

    /* ── bitrate for the hardware lane (bits/s; 0 = the pipeline decides) ──
       A same-size re-encode (HEVC → H.264 for «plays everywhere», a quality
       pick) keeps the pipeline's own table and its source-aware bounds. A
       DOWNSCALE is sized here, for its OUTPUT, because the converter adds what
       the pipeline's floor does not: the quality pick (low / medium / high),
       at the source's full video bitrate rather than 85 % of the file's. The
       size itself is the pipeline's own formula, not a copy:
       WebCodecsPipeline.sourceBpsForOutput — the source's video bitrate scaled
       by the pixel ratio to the 0.75 power (smaller frames need more bits per
       pixel), ×1.7 when the source codec is one H.264 needs more bits to match
       (HEVC / VP9 / AV1; this function used ×1.4 of its own until the pipeline
       took the factor over — the converter's HEVC downscales now ask 21 %
       more). Until 2026-09-30 the pipeline floored every output
       at 85 % of the FULL-SIZE source bitrate («720p» of a 12 Mbit/s 1080p
       clip came out 11.5 Mbit/s — 29 MB from a 31 MB source), and this
       function carried its own exponent and gain to get around it; the
       pipeline now sizes its floor with the same function. No pipeline on the
       page (or a stale cached one without the function) — 0, and the lane's
       own bounds decide. */
    function hwBitrate(probe, plan, o) {
        o = o || {};
        if (o.targetSizeMb > 0 && probe && probe.duration > 0) {
            return bitrateForSize(o.targetSizeMb, probe.duration, o.audioKbps) * 1000;
        }
        if (!plan || !(plan.scaleTo > 0) || !probe) return 0;
        var P = window.WebCodecsPipeline;
        if (!P || typeof P.sourceBpsForOutput !== 'function') return 0;
        var srcPx = (+probe.width || 0) * (+probe.height || 0);
        var srcBps = (probe.size > 0 && probe.duration > 0)
            ? probe.size * 8 / probe.duration - (o.audioKbps > 0 ? o.audioKbps : 128) * 1000 : 0;
        if (!(srcPx > 0) || !(srcBps > 0)) return 0;
        var bps = P.sourceBpsForOutput(srcBps, srcPx, outPixels(probe, plan), normCodec(probe.codec, 'video'));
        var q = (o.crf && o.crf <= 20) ? 1.25 : (o.crf && o.crf >= 28) ? 0.6 : 1;
        return Math.max(600000, Math.round(bps * q));
    }

    /* ── time, from the lane that will actually run ───────────────────────
       The previous estimate multiplied by the WebCodecs speed (0.25×) whenever
       the browser HAD WebCodecs — while the tool encoded everything with
       single-threaded x264: «~7 s» on the plan, 204–307 s on the clock
       (1080×1920, 15 s), «~6 s» for runs that took 200 s to 44 min — ×30–440
       too optimistic on a desktop (measured 2026-09-30), worse on a phone.
       Now: bytes for a copy, media seconds for an audio re-encode,
       pixels for a video re-encode — each at the rate of ITS engine, measured
       on real files, scaled by device class. The number is a promise made
       before the click; while the job runs the page replaces it with the
       live estimate (__acETA) from real progress.
       Rates measured 2026-09-30 (Chrome 1xx desktop, real 1080p/4K/portrait
       files through the tool's own UI; machine under foreign load):
         copy   31 MB → 3.1 s, 20 MB → 1.2 s; G.711 → AAC of 20 s → 4–6.5 s
         x264   single-thread: 0.92 Mpx out × 601 fr → 200 s; 2.07 Mpx × 601 →
                701 s (CRF 20); 0.66 Mpx × 451 → 204–307 s ⇒ ~1.6–2.6 Mpx/s of
                OUTPUT; a 4K HEVC source adds ~2–4 s/frame of WASM decode
         hw     WebCodecsPipeline: 1080→720 601 fr 8.8–9.3 s, 1080→1080 5.9 s,
                4K HEVC→1080 6.7 s, 4K→4K 8.8 s (per frame 7–29 ms)
       and from prod telemetry: the same lane in /video-speed (hw_used, 10 d,
       279 runs) — desktop p50 10 / p75 19 ms per frame, phone p50 25 / p75 48;
       this tool's full re-encodes cost 5.9 s per source MB on desktop and
       19.0 on phones (p50) ⇒ phone ×2.5 on hardware, ×3.2 on x264. The
       hardware rate is the prod p75 on purpose: an estimate is a promise. */
    var RATE = {
        copyMBps: 25,          // stream copy, MB of source per second
        copyFixedS: 1,         // container write + faststart pass
        audioX: 0.1,           // audio re-encode, seconds per media second (+ copy)
        wasmEncMpxps: 1.8,     // x264 'fast', single-thread WASM, OUTPUT Mpx per second
        wasmMtX: 5,            // multi-thread core on an isolated page (coep_test.php A/B: 5–5.7×)
        wasmMpeg4X: 20,        // MPEG-4 Part 2 encoder (AVI) vs x264, per output pixel
        wasmDecH264Mpxps: 20,  // WASM decode of an H.264 source, Mpx per second
        wasmDecOtherMpxps: 4,  // …of HEVC / VP9 / AV1 / anything else
        hwMsPerFrame: 19,      // hardware lane at 1080p-equivalent pixels, ms per frame
        hwFixedS: 2            // demux + encoder configure + mux
    };
    var DEVICE = { phone: { copy: 2.2, wasm: 3.2, hw: 2.5 }, desk: { copy: 1, wasm: 1, hw: 1 } };

    function isPhone() {
        try { return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || ''); } catch (_) { return false; }
    }

    /** Output size (pixels per frame) a plan will encode. */
    function outPixels(probe, plan) {
        var w = (probe && +probe.width) || 1920, h = (probe && +probe.height) || 1080;
        var s = Math.min(w, h);
        if (plan && plan.scaleTo > 0 && s > plan.scaleTo) { var k = plan.scaleTo / s; w = Math.round(w * k); h = Math.round(h * k); }
        return w * h;
    }

    /**
     * @param {object} probe
     * @param {object} plan   decide() result; plan.lane = 'hw' | 'wasm' for 'full'
     * @param {object} caps   capabilities() (+ caps.mt when the MT core will run)
     * @returns {number} seconds (0 when the duration is unknown)
     */
    function estimateSeconds(probe, plan, caps) {
        var dur = (probe && probe.duration) || 0;
        if (!dur || !plan) return 0;
        var dev = DEVICE[(caps && caps.phone != null ? caps.phone : isPhone()) ? 'phone' : 'desk'];
        var mb = ((probe && probe.size) || 0) / 1048576;
        var copyS = RATE.copyFixedS + (mb / RATE.copyMBps) * dev.copy;
        if (plan.kind === 'remux') return Math.max(1, Math.round(copyS));
        if (plan.kind === 'audio') return Math.max(1, Math.round(copyS + dur * RATE.audioX * dev.copy));
        var frames = dur * (((probe && +probe.fps) > 0) ? +probe.fps : 30);
        var outMpx = outPixels(probe, plan) / 1e6;
        var srcMpx = ((probe && +probe.width) || 1920) * ((probe && +probe.height) || 1080) / 1e6;
        if (plan.lane === 'hw') {
            // Decode and encode run on the GPU in parallel; the bigger side of
            // the work sets the pace, and it grows slower than linearly with
            // pixels (4K ≈ 2.6× a 1080p frame, measured 29 vs 10 ms).
            var px = Math.max(outMpx, srcMpx * 0.5) / 2.0736;
            var msPerFrame = RATE.hwMsPerFrame * Math.pow(Math.max(0.1, px), 0.7);
            return Math.max(2, Math.round(RATE.hwFixedS + frames * msPerFrame / 1000 * dev.hw));
        }
        // MPEG-4 Part 2 (the AVI lane) is a fraction of x264's cost per pixel:
        // 601 frames of 1080p in 82 s against ~700 s — the decode is most of it.
        var encX = /^(mpeg4|msmpeg4)/.test(String(plan.encoder || '')) ? RATE.wasmMpeg4X : 1;
        var enc = RATE.wasmEncMpxps * encX * ((caps && caps.mt) ? RATE.wasmMtX : 1);
        var dec = normCodec(probe && probe.codec, 'video') === 'h264' ? RATE.wasmDecH264Mpxps : RATE.wasmDecOtherMpxps;
        return Math.max(2, Math.round((frames * outMpx / enc + frames * srcMpx / dec) * dev.wasm));
    }

    window.VideoConvertPlan = {
        capabilities: capabilities,
        decide: decide,
        scaleFilter: scaleFilter,
        hwSkipReason: hwSkipReason,
        hwBitrate: hwBitrate,
        h264Plays: h264Plays,
        bitrateForSize: bitrateForSize,
        estimateSize: estimateSize,
        estimateSeconds: estimateSeconds,
        _RATE: RATE
    };
})();
